import { Tag } from '@douyinfe/semi-ui';

export function renderText(text, limit) {
  if (text.length > limit) {
    return text.slice(0, limit - 3) + '...';
  }
  return text;
}

/**
 * Render group tags based on the input group string
 * @param {string} group - The input group string
 * @returns {JSX.Element} - The rendered group tags
 */
export function renderGroup(group) {
  if (group === '') {
    return (
      <Tag size='large' key='default'>
        unknown
      </Tag>
    );
  }

  const tagColors = {
    vip: 'yellow',
    pro: 'yellow',
    svip: 'red',
    premium: 'red',
  };

  const groups = group.split(',').sort();

  return (
    <span key={group}>
      {groups.map((group) => (
        <Tag
          size='large'
          color={tagColors[group] || stringToColor(group)}
          key={group}
        >
          {group}
        </Tag>
      ))}
    </span>
  );
}

export function renderNumber(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 10000) {
    return (num / 1000).toFixed(1) + 'k';
  } else {
    return num;
  }
}

export function renderQuotaNumberWithDigit(num, digits = 2) {
  let displayInCurrency = localStorage.getItem('display_in_currency');
  num = num.toFixed(digits);
  if (displayInCurrency) {
    return '$' + num;
  }
  return num;
}

export function renderNumberWithPoint(num) {
  num = num.toFixed(2);
  if (num >= 100000) {
    // Convert number to string to manipulate it
    let numStr = num.toString();
    // Find the position of the decimal point
    let decimalPointIndex = numStr.indexOf('.');

    let wholePart = numStr;
    let decimalPart = '';

    // If there is a decimal point, split the number into whole and decimal parts
    if (decimalPointIndex !== -1) {
      wholePart = numStr.slice(0, decimalPointIndex);
      decimalPart = numStr.slice(decimalPointIndex);
    }

    // Take the first two and last two digits of the whole number part
    let shortenedWholePart = wholePart.slice(0, 2) + '..' + wholePart.slice(-2);

    // Return the formatted number
    return shortenedWholePart + decimalPart;
  }

  // If the number is less than 100,000, return it unmodified
  return num;
}

export function getQuotaPerUnit() {
  let quotaPerUnit = localStorage.getItem('quota_per_unit');
  quotaPerUnit = parseFloat(quotaPerUnit);
  return quotaPerUnit;
}

export function renderUnitWithQuota(quota) {
  let quotaPerUnit = localStorage.getItem('quota_per_unit');
  quotaPerUnit = parseFloat(quotaPerUnit);
  quota = parseFloat(quota);
  return quotaPerUnit * quota;
}

export function getQuotaWithUnit(quota, digits = 6) {
  let quotaPerUnit = localStorage.getItem('quota_per_unit');
  quotaPerUnit = parseFloat(quotaPerUnit);
  return (quota / quotaPerUnit).toFixed(digits);
}

export function renderQuotaWithAmount(amount) {
  let displayInCurrency = localStorage.getItem('display_in_currency');
  displayInCurrency = displayInCurrency === 'true';
  if (displayInCurrency) {
    return '$' + amount;
  } else {
    return renderUnitWithQuota(amount);
  }
}

export function renderQuota(quota, digits = 2) {
  let quotaPerUnit = 500000;
  return '$' + (quota / quotaPerUnit).toFixed(digits);
}

/**
 * 解析 new-api-horizon 的 tier 表达式（base64 编码）。
 * 例如:
 *   len <= 272000 ? tier("standard", p * 2.5 + c * 15 + cr * 0.25)
 *                 : tier("long_context", p * 5 + c * 22.5 + cr * 0.5)
 *
 * @returns {Array<{name, cond, p, c, cr, cw}>|null}
 *   每档系数（系数 × $2/M = USD/M）
 */
export function parseTierExpr(b64) {
  if (!b64 || typeof b64 !== 'string') return null;
  let expr;
  try {
    // expr_b64 内容是纯 ASCII，atob 即可
    expr = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf8');
  } catch (e) { return null; }
  if (!expr || !expr.includes('tier(')) return null;

  // 提取每个 tier(name, body)
  const tierRe = /tier\(\s*"([^"]+)"\s*,\s*([^)]+?)\s*\)/g;
  const found = [];
  let m;
  while ((m = tierRe.exec(expr)) !== null) {
    found.push({ name: m[1], body: m[2] });
  }
  if (!found.length) return null;

  // 解析条件 (例: len <= 272000)
  const condMatch = expr.match(/\blen\s*(<=|<|>=|>)\s*(\d+)/);
  const condOp = condMatch ? condMatch[1] : null;
  const condVal = condMatch ? parseInt(condMatch[2], 10) : null;
  const fmtLen = (n) => {
    if (n == null) return '';
    if (n >= 1000) return `${n / 1000}K`;
    return String(n);
  };
  // ?: 真分支 = 第 0 个 tier；假分支 = 第 1 个 tier
  const flipOp = (op) => ({ '<=': '>', '<': '>=', '>=': '<', '>': '<=' }[op] || op);

  const extractCoef = (body, varName) => {
    // 数字 * v 或 v * 数字
    const re = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*\\*\\s*\\b${varName}\\b|\\b${varName}\\b\\s*\\*\\s*(\\d+(?:\\.\\d+)?)`);
    const mm = body.match(re);
    return mm ? parseFloat(mm[1] || mm[2]) : 0;
  };

  return found.map((t, i) => {
    let cond;
    if (condOp != null) {
      const op = i === 0 ? condOp : flipOp(condOp);
      cond = `Length ${op} ${fmtLen(condVal)}`;
    }
    return {
      name: t.name,
      cond,
      p: extractCoef(t.body, 'p'),
      c: extractCoef(t.body, 'c'),
      cr: extractCoef(t.body, 'cr'),
      cw: extractCoef(t.body, 'cw'),
    };
  });
}

export function renderModelPrice(
  inputTokens,
  completionTokens,
  modelRatio,
  modelPrice = -1,
  completionRatio,
  groupRatio,
  cacheTokens = 0,
  cacheRatio = 0,
  cacheCreationTokens = 0,
  cacheCreationRatio = 0,
  extra = {},
) {
  const formatNum = (n) => {
    if (n == null) return 0;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n;
  };

  // 0. new-api-horizon 动态计费（分阶表达式 tiered_expr）：
  //    解析 expr_b64 拿到每档单价（系数 × $2/M = USD/M），
  //    高亮 matched_tier 命中阶梯，配合 token 明细可以清楚看出价格是怎么算出来的。
  const isTieredExpr = extra.billing_mode === 'tiered_expr'
    || extra.billing_source === 'subscription';
  if (isTieredExpr) {
    const tiers = parseTierExpr(extra.expr_b64);
    const matched = extra.matched_tier;
    const matchedTier = tiers && tiers.find((t) => t.name === matched);
    const groupRatioVal = extra.group_ratio != null ? Number(extra.group_ratio) : 1;
    const quota = extra.quota;

    // new-api-horizon tier 表达式中的系数 = USD / 1M tokens（即 5 表示 $5/M）。
    // 已通过 wallet 模式的真实日志反算验证：
    //   prompt=1283 × $5/M + completion=62 × $30/M + cache=173440 × $0.5/M
    //     = 0.006415 + 0.00186 + 0.08672 = $0.094995 ≈ quota/500000 = $0.094996 ✓
    const fmtPriceM = (coef) => {
      if (coef == null || coef === 0) return '-';
      const v = coef * groupRatioVal;
      const s = v >= 10 ? v.toFixed(0)
        : v >= 1 ? v.toFixed(1).replace(/\.0$/, '')
        : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
      return `$${s}/M`;
    };

    const rowStyle = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '3px 0',
      fontSize: 12,
    };
    const labelStyle = { color: 'var(--semi-color-text-2)' };
    const valueStyle = {
      color: 'var(--semi-color-text-0)',
      fontFamily: 'monospace',
      fontWeight: 500,
    };
    const sectionTitle = {
      fontSize: 11,
      fontWeight: 700,
      color: 'var(--semi-color-text-1)',
      marginTop: 10,
      marginBottom: 4,
      paddingBottom: 3,
      borderBottom: '1px solid var(--semi-color-border)',
    };

    return (
      <div style={{ minWidth: 280, padding: '4px 2px' }}>
        {/* 顶部：动态计费 + 命中档位 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingBottom: 6,
          borderBottom: '1px solid var(--semi-color-border)',
        }}>
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            background: 'var(--semi-color-success)',
            padding: '2px 8px',
            borderRadius: 4,
          }}>动态计费</span>
          {matched && (
            <>
              <span style={{ fontSize: 11, color: 'var(--semi-color-text-2)' }}>命中</span>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--semi-color-success)',
                background: 'var(--semi-color-success-light-default)',
                padding: '2px 8px',
                borderRadius: 4,
              }}>{matched}</span>
            </>
          )}
        </div>

        {/* Token 明细 */}
        <div style={sectionTitle}>Token 明细</div>
        <div style={rowStyle}>
          <span style={labelStyle}>输入 Token</span>
          <span style={valueStyle}>{Number(inputTokens || 0).toLocaleString()}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>输出 Token</span>
          <span style={valueStyle}>{Number(completionTokens || 0).toLocaleString()}</span>
        </div>
        {cacheTokens > 0 && (
          <div style={rowStyle}>
            <span style={labelStyle}>缓存读取</span>
            <span style={valueStyle}>{Number(cacheTokens).toLocaleString()}</span>
          </div>
        )}
        {cacheCreationTokens > 0 && (
          <div style={rowStyle}>
            <span style={labelStyle}>缓存写入</span>
            <span style={valueStyle}>{Number(cacheCreationTokens).toLocaleString()}</span>
          </div>
        )}

        {/* 命中档位的单价（怎么算出来的） */}
        {matchedTier && (
          <>
            <div style={sectionTitle}>计费详情（命中档位）</div>
            <div style={rowStyle}>
              <span style={labelStyle}>输入单价</span>
              <span style={valueStyle}>{fmtPriceM(matchedTier.p)}</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>输出单价</span>
              <span style={valueStyle}>{fmtPriceM(matchedTier.c)}</span>
            </div>
            {matchedTier.cr > 0 && (
              <div style={rowStyle}>
                <span style={labelStyle}>缓存读取</span>
                <span style={valueStyle}>{fmtPriceM(matchedTier.cr)}</span>
              </div>
            )}
            {matchedTier.cw > 0 && (
              <div style={rowStyle}>
                <span style={labelStyle}>缓存写入</span>
                <span style={valueStyle}>{fmtPriceM(matchedTier.cw)}</span>
              </div>
            )}
            <div style={rowStyle}>
              <span style={labelStyle}>分组倍率</span>
              <span style={valueStyle}>{groupRatioVal.toFixed(4)}x</span>
            </div>
          </>
        )}

        {/* 分档价格表 */}
        {tiers && tiers.length > 1 && (
          <>
            <div style={sectionTitle}>分档价格表</div>
            <div style={{ fontSize: 11 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
                gap: 6,
                color: 'var(--semi-color-text-2)',
                fontSize: 10,
                paddingBottom: 3,
                marginBottom: 2,
                borderBottom: '1px dashed var(--semi-color-border)',
              }}>
                <div>档位</div>
                <div style={{ textAlign: 'right' }}>输入</div>
                <div style={{ textAlign: 'right' }}>输出</div>
                <div style={{ textAlign: 'right' }}>缓存</div>
              </div>
              {tiers.map((t) => {
                const hit = t.name === matched;
                return (
                  <div key={t.name} style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 1fr 1fr 1fr',
                    gap: 6,
                    padding: '3px 4px',
                    borderRadius: 4,
                    background: hit ? 'var(--semi-color-success-light-default)' : 'transparent',
                    fontFamily: 'monospace',
                    color: hit ? 'var(--semi-color-success)' : 'var(--semi-color-text-0)',
                    fontWeight: hit ? 600 : 400,
                  }}>
                    <div>
                      {hit && '✓ '}{t.name}
                      {t.cond && (
                        <div style={{
                          fontSize: 9,
                          color: 'var(--semi-color-text-2)',
                          fontFamily: 'inherit',
                          fontWeight: 400,
                        }}>
                          {t.cond}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>{fmtPriceM(t.p)}</div>
                    <div style={{ textAlign: 'right' }}>{fmtPriceM(t.c)}</div>
                    <div style={{ textAlign: 'right' }}>{fmtPriceM(t.cr)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* 总费用 */}
        {quota != null && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 10,
            paddingTop: 8,
            borderTop: '1px solid var(--semi-color-border)',
          }}>
            <span style={{
              color: 'var(--semi-color-text-0)',
              fontSize: 12,
              fontWeight: 600,
            }}>总费用</span>
            <span style={{
              color: 'var(--semi-color-success)',
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'monospace',
            }}>
              ${(Number(quota) / 500000).toFixed(6)}
            </span>
          </div>
        )}
      </div>
    );
  }

  // 1 ratio = $0.002 / 1K tokens
  // 仅当 modelPrice > 0 时才视为按次计费（固定价格）；
  // NewAPI 不同版本对"未启用按次价格"的标记不同：旧版用 -1，新版直接为 0/未设置。
  if (modelPrice && modelPrice > 0) {
    return (
      <div style={{ padding: '8px 0' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#10b981' }}>
          ${(modelPrice * groupRatio).toFixed(4)}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>固定价格</div>
      </div>
    );
  }

  if (completionRatio === undefined) completionRatio = 0;
  
  // 这里的 *2 是因为 1倍率=0.002刀
  const inputPrice = modelRatio * 2.0 * groupRatio;
  const completionPrice = modelRatio * 2.0 * completionRatio * groupRatio;
  const cacheReadPrice = modelRatio * 2.0 * cacheRatio * groupRatio;
  const cacheWritePrice = modelRatio * 2.0 * cacheCreationRatio * groupRatio;
  
  const promptCost = (inputTokens / 1000000) * inputPrice;
  const cacheReadCost = (cacheTokens / 1000000) * cacheReadPrice;
  const cacheWriteCost = (cacheCreationTokens / 1000000) * cacheWritePrice;
  const completionCost = (completionTokens / 1000000) * completionPrice;
  const totalPrice = promptCost + cacheReadCost + cacheWriteCost + completionCost;
  const billedPrice = Number.isFinite(Number(extra.quota))
    ? Number(extra.quota) / 500000
    : totalPrice;

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    fontSize: 12,
    borderBottom: '1px solid var(--semi-color-border)',
  };
  
  const labelStyle = {
    color: 'var(--semi-color-text-1)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  };
  const valueStyle = {
    color: 'var(--semi-color-text-0)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 1,
    fontFamily: 'monospace',
    fontWeight: 500,
  };
  const priceStyle = { color: 'var(--semi-color-text-2)', fontSize: 10 };
  
  return (
    <div style={{ minWidth: 200, padding: '4px 0' }}>
      {/* 提示 */}
      <div style={rowStyle}>
        <span style={labelStyle}>
          <span style={{ color: '#f59e0b' }}>●</span> 普通输入
        </span>
        <span style={valueStyle}>
          <span>{formatNum(inputTokens)}</span>
          <span style={priceStyle}>
            × ${inputPrice.toFixed(2)}/M = ${promptCost.toFixed(6)}
          </span>
        </span>
      </div>
      
      {/* 缓存命中 */}
      {cacheTokens > 0 && (
        <div style={rowStyle}>
          <span style={labelStyle}>
            <span style={{ color: '#10b981' }}>↓</span> 缓存命中
          </span>
          <span style={valueStyle}>
            <span>{formatNum(cacheTokens)}</span>
            <span style={priceStyle}>
              × ${cacheReadPrice.toFixed(2)}/M = ${cacheReadCost.toFixed(6)}
            </span>
          </span>
        </div>
      )}
      
      {/* 缓存创建 */}
      {cacheCreationTokens > 0 && (
        <div style={rowStyle}>
          <span style={labelStyle}>
            <span style={{ color: '#3b82f6' }}>↑</span> 缓存创建
          </span>
          <span style={valueStyle}>
            <span>{formatNum(cacheCreationTokens)}</span>
            <span style={priceStyle}>
              × ${cacheWritePrice.toFixed(2)}/M = ${cacheWriteCost.toFixed(6)}
            </span>
          </span>
        </div>
      )}
      
      {/* 补全 */}
      <div style={rowStyle}>
        <span style={labelStyle}>
          <span style={{ color: '#8b5cf6' }}>●</span> 补全
        </span>
        <span style={valueStyle}>
          <span>{formatNum(completionTokens)}</span>
          <span style={priceStyle}>
            × ${completionPrice.toFixed(2)}/M = ${completionCost.toFixed(6)}
          </span>
        </span>
      </div>
      
      {/* 总计 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginTop: 8,
        paddingTop: 8,
        borderTop: '1px solid var(--semi-color-border)',
      }}>
        <span style={{
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--semi-color-text-0)',
          fontSize: 12,
          fontWeight: 600,
        }}>
          实扣总计
          <small style={{ color: 'var(--semi-color-text-2)', fontSize: 9, fontWeight: 400 }}>
            按账单额度结算
          </small>
        </span>
        <span style={{ 
          color: '#10b981', 
          fontSize: 14, 
          fontWeight: 600,
          fontFamily: 'monospace',
        }}>
          ${billedPrice.toFixed(6)}
        </span>
      </div>
    </div>
  );
}

export function renderQuotaWithPrompt(quota, digits) {
  let displayInCurrency = localStorage.getItem('display_in_currency');
  displayInCurrency = displayInCurrency === 'true';
  if (displayInCurrency) {
    return `（等价金额：${renderQuota(quota, digits)}）`;
  }
  return '';
}

const colors = [
  'amber',
  'blue',
  'cyan',
  'green',
  'grey',
  'indigo',
  'light-blue',
  'lime',
  'orange',
  'pink',
  'purple',
  'red',
  'teal',
  'violet',
  'yellow',
];

export const modelColorMap = {
  'dall-e': 'rgb(147,112,219)', // 深紫色
  // 'dall-e-2': 'rgb(147,112,219)', // 介于紫色和蓝色之间的色调
  'dall-e-3': 'rgb(153,50,204)', // 介于紫罗兰和洋红之间的色调
  'gpt-3.5-turbo': 'rgb(184,227,167)', // 浅绿色
  // 'gpt-3.5-turbo-0301': 'rgb(131,220,131)', // 亮绿色
  'gpt-3.5-turbo-0613': 'rgb(60,179,113)', // 海洋绿
  'gpt-3.5-turbo-1106': 'rgb(32,178,170)', // 浅海洋绿
  'gpt-3.5-turbo-16k': 'rgb(149,252,206)', // 淡橙色
  'gpt-3.5-turbo-16k-0613': 'rgb(119,255,214)', // 淡桃色
  'gpt-3.5-turbo-instruct': 'rgb(175,238,238)', // 粉蓝色
  'gpt-4': 'rgb(135,206,235)', // 天蓝色
  // 'gpt-4-0314': 'rgb(70,130,180)', // 钢蓝色
  'gpt-4-0613': 'rgb(100,149,237)', // 矢车菊蓝
  'gpt-4-1106-preview': 'rgb(30,144,255)', // 道奇蓝
  'gpt-4-0125-preview': 'rgb(2,177,236)', // 深天蓝
  'gpt-4-turbo-preview': 'rgb(2,177,255)', // 深天蓝
  'gpt-4-32k': 'rgb(104,111,238)', // 中紫色
  // 'gpt-4-32k-0314': 'rgb(90,105,205)', // 暗灰蓝色
  'gpt-4-32k-0613': 'rgb(61,71,139)', // 暗蓝灰色
  'gpt-4-all': 'rgb(65,105,225)', // 皇家蓝
  'gpt-4-gizmo-*': 'rgb(0,0,255)', // 纯蓝色
  'gpt-4-vision-preview': 'rgb(25,25,112)', // 午夜蓝
  'text-ada-001': 'rgb(255,192,203)', // 粉红色
  'text-babbage-001': 'rgb(255,160,122)', // 浅珊瑚色
  'text-curie-001': 'rgb(219,112,147)', // 苍紫罗兰色
  // 'text-davinci-002': 'rgb(199,21,133)', // 中紫罗兰红色
  'text-davinci-003': 'rgb(219,112,147)', // 苍紫罗兰色（与Curie相同，表示同一个系列）
  'text-davinci-edit-001': 'rgb(255,105,180)', // 热粉色
  'text-embedding-ada-002': 'rgb(255,182,193)', // 浅粉红
  'text-embedding-v1': 'rgb(255,174,185)', // 浅粉红色（略有区别）
  'text-moderation-latest': 'rgb(255,130,171)', // 强粉色
  'text-moderation-stable': 'rgb(255,160,122)', // 浅珊瑚色（与Babbage相同，表示同一类功能）
  'tts-1': 'rgb(255,140,0)', // 深橙色
  'tts-1-1106': 'rgb(255,165,0)', // 橙色
  'tts-1-hd': 'rgb(255,215,0)', // 金色
  'tts-1-hd-1106': 'rgb(255,223,0)', // 金黄色（略有区别）
  'whisper-1': 'rgb(245,245,220)', // 米色
  'claude-3-opus-20240229': 'rgb(255,132,31)', // 橙红色
  'claude-3-sonnet-20240229': 'rgb(253,135,93)', // 橙色
  'claude-3-haiku-20240307': 'rgb(255,175,146)', // 浅橙色
  'claude-2.1': 'rgb(255,209,190)', // 浅橙色（略有区别）
};

export function stringToColor(str) {
  let sum = 0;
  // 对字符串中的每个字符进行操作
  for (let i = 0; i < str.length; i++) {
    // 将字符的ASCII值加到sum中
    sum += str.charCodeAt(i);
  }
  // 使用模运算得到个位数
  let i = sum % colors.length;
  return colors[i];
}
